import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const sitemapSections = [
  {
    title: 'Main Pages',
    icon: 'ri-home-line',
    links: [
      { label: 'Home', path: '/' },
      { label: 'About Us', path: '/about' },
      { label: 'Contact Us', path: '/contact' },
      { label: 'Reviews', path: '/reviews' },
      { label: 'FAQ — Items We Recycle', path: '/faq' },
    ],
  },
  {
    title: 'Services',
    icon: 'ri-recycle-line',
    links: [
      { label: 'All Services', path: '/services' },
      { label: 'TV Recycling Los Angeles', path: '/tv-recycling' },
      { label: 'Computer Recycling LA', path: '/computer-recycling' },
      { label: 'Battery Recycling LA', path: '/battery-recycling' },
      { label: 'EV Charger Recycling LA', path: '/ev-charging-recycling' },
      { label: 'Hard Drive Destruction', path: '/hard-drive-destruction' },
    ],
  },
  {
    title: 'Schedule & Locations',
    icon: 'ri-map-pin-line',
    links: [
      { label: 'Request Pickup', path: '/pickup-request' },
      { label: 'Drop-Off Form', path: '/dropoff-form' },
      { label: 'Drop-Off Locations', path: '/locations' },
      { label: 'W 3rd Street Location', path: '/locations/west-3rd-street' },
      { label: 'Slauson Ave Location', path: '/locations/slauson-ave' },
      { label: 'E-Waste Events', path: '/events' },
      { label: 'Service Areas', path: '/service-areas' },
    ],
  },
  {
    title: 'Resources & Guides',
    icon: 'ri-book-open-line',
    links: [
      { label: 'E-Waste Guide & Glossary', path: '/e-waste-guide' },
      { label: 'Business Sustainability', path: '/sustainability-report' },
      { label: 'Blog & Resources', path: '/blog' },
      { label: 'Reviews', path: '/reviews' },
    ],
  },
  {
    title: 'Legal',
    icon: 'ri-shield-check-line',
    links: [
      { label: 'Privacy Policy', path: '/privacy-policy' },
      { label: 'Terms of Service', path: '/terms-of-service' },
      { label: 'Sitemap', path: '/sitemap' },
    ],
  },
  {
    title: 'City Service Pages',
    icon: 'ri-building-line',
    links: [
      { label: 'Electronics Recycling — Downtown Los Angeles', path: '/electronics-recycling-downtown-los-angeles' },
      { label: 'Electronics Recycling — Santa Monica', path: '/electronics-recycling-santa-monica' },
      { label: 'Electronics Recycling — Burbank', path: '/electronics-recycling-burbank' },
      { label: 'Electronics Recycling — Glendale', path: '/electronics-recycling-glendale' },
      { label: 'Electronics Recycling — Pasadena', path: '/electronics-recycling-pasadena' },
      { label: 'Electronics Recycling — Long Beach', path: '/electronics-recycling-long-beach' },
      { label: 'Electronics Recycling — Culver City', path: '/electronics-recycling-culver-city' },
      { label: 'Electronics Recycling — Torrance', path: '/electronics-recycling-torrance' },
      { label: 'Electronics Recycling — Inglewood', path: '/electronics-recycling-inglewood' },
      { label: 'Electronics Recycling — Redondo Beach', path: '/electronics-recycling-redondo-beach' },
      { label: 'Electronics Recycling — Van Nuys', path: '/electronics-recycling-van-nuys' },
      { label: 'Electronics Recycling — Hawthorne', path: '/electronics-recycling-hawthorne' },
    ],
  },
];

const SitemapPage = () => (
  <>
    <title>Sitemap | Greenly Electronics Recycling Los Angeles</title>
    <meta name="description" content="Browse all pages on the Greenly Electronics Recycling website — services, city locations, drop-off info, FAQ & more across Los Angeles County." />
    <meta name="robots" content="index, follow" />
    <meta name="last-modified" content="2026-05-11" />
    <link rel="canonical" href={`${SITE_URL}/sitemap`} />
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/sitemap#webpage`,
          "url": `${SITE_URL}/sitemap`,
          "name": "Sitemap | Greenly Electronics Recycling Los Angeles",
          "description": "Full sitemap for Greenly Electronics Recycling — browse all pages including services, city locations, drop-off info, and more across Los Angeles County.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": { "@id": `${SITE_URL}/sitemap#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${SITE_URL}/sitemap#breadcrumb`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": `${SITE_URL}/sitemap` }
          ]
        }
      ]
    })}</script>
    <Navbar />
    <main>
      {/* Hero */}
      <section className="pt-16" style={{ background: 'linear-gradient(135deg, #f0faf4 0%, #e8f5ee 40%, #f5fbf7 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#1B6B3A] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-700 font-medium">Sitemap</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
            Site<span className="text-[#1B6B3A]">map</span>
          </h1>
          <p className="text-gray-500 text-base max-w-xl">
            A complete directory of all pages on the Greenly Electronics Recycling website — find what you need quickly.
          </p>
        </div>
      </section>

      {/* Sitemap Grid */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {sitemapSections.map((section) => (
              <div key={section.title}>
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-lg">
                    <i className={`${section.icon} text-[#1B6B3A] text-sm`}></i>
                  </div>
                  <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{section.title}</h2>
                </div>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={link.path} className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#4CAF82] text-sm mt-0.5 shrink-0"></i>
                      <Link
                        to={link.path}
                        className="text-gray-600 text-sm hover:text-[#1B6B3A] transition-colors cursor-pointer leading-snug"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 mt-14 pt-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs text-gray-400 mb-1">Looking for the XML sitemap for search engines?</p>
                <a
                  href="https://greenlyelectronicsrecycling.com/sitemap.xml"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-[#1B6B3A] font-semibold hover:underline cursor-pointer"
                >
                  <i className="ri-file-code-line"></i>
                  View sitemap.xml
                </a>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-circle-line"></i>
                Request Service
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default SitemapPage;
